// Boundary de render. Captura throws síncronos em React (hook ou render)
// e mostra fallback amigável + reporta pro Sentry.
//
// Usado em duas posições:
//   1. Global (App.tsx wrap do Router) — pega tudo que escape de boundaries
//      mais internos.
//   2. Por rota crítica (live admin, wizard, live público) — fallback
//      contextualizado e botão "tentar de novo" sem recarregar tudo.
//
// IMPORTANTE: ErrorBoundary não pega erros de:
//   - event handlers (use try/catch + captureError)
//   - código async (Promise rejection) — wirado em main.tsx via
//     window.addEventListener('unhandledrejection').

import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError, reloadForStaleChunk } from '@/lib/chunkReload';
import { commonT } from '@/i18n';
import { shellT } from '@/i18n/messages/shell';

interface Props {
  /// Nome da boundary, vira tag no Sentry — facilita filtrar "qual área
  /// quebrou" no painel sem precisar adivinhar do stack.
  name: string;
  /// Mensagem amigável pro usuário. Default: genérica.
  fallbackTitle?: string;
  fallbackHint?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  resetKey: number;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, resetKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Chunk lazy que não carregou (stale deploy) não é bug da tela — é o
    // index.html velho pedindo um hash que o deploy seguinte apagou. Fallback
    // pro caminho do `vite:preloadError` (que só dispara no bundle de prod):
    // recarrega 1x e não polui o Sentry com um "erro" esperado e recuperável.
    if (isChunkLoadError(error)) {
      const reloading = reloadForStaleChunk();
      if (reloading) return;
      // Guard impediu o reload (já recarregamos e falhou de novo) → deploy
      // genuinamente quebrado. Aí sim reporta, pra aparecer no painel.
    }

    Sentry.withScope((scope) => {
      scope.setTag('boundary', this.props.name);
      scope.setContext('react', {
        componentStack: info.componentStack ?? null,
      });
      Sentry.captureException(error);
    });
  }

  reset = () => {
    this.setState((s) => ({ error: null, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (this.state.error) {
      // Chunk stale: o reload já foi disparado no componentDidCatch. Mostramos
      // uma mensagem de "atualizando" no flash até a página trocar — e o mesmo
      // texto caso o guard tenha impedido o reload (deploy quebrado), onde os
      // botões abaixo dão a saída manual.
      const isChunk = isChunkLoadError(this.state.error);
      const t = shellT().errorBoundary;
      return (
        <div className="flex min-h-[60vh] items-center justify-center px-4">
          <div className="max-w-md space-y-4 text-center">
            <h2 className="text-xl font-semibold">
              {this.props.fallbackTitle ?? (isChunk ? t.updatingTitle : t.errorTitle)}
            </h2>
            <p className="text-sm text-muted-foreground">
              {this.props.fallbackHint ?? (isChunk ? t.updatingHint : t.errorHint)}
            </p>
            <details className="rounded-md border bg-muted/30 p-3 text-left text-xs">
              <summary className="cursor-pointer text-muted-foreground">{t.technicalDetails}</summary>
              <p className="mt-2 font-mono break-words">{this.state.error.message}</p>
            </details>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button type="button" variant="outline" onClick={() => location.reload()}>
                {t.reloadPage}
              </Button>
              <Button type="button" onClick={this.reset}>
                {commonT().retry}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return <div key={this.state.resetKey}>{this.props.children}</div>;
  }
}
